/*
 * File: QuestionCard.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 3:01 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo,
	useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Parser } from 'html-to-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useTranslation } from 'react-i18next';

// Utilities
import { formatNumber } from '../../../utilities/utilities';
import { generateShortFormContentPlaceholder, generateTitlePlaceholder } from '../../utilities/placeholder';

// Services
import { recordUpvote, removeUpvote } from '../../services/question';

// Store
import { updateUser } from '../../store/slices/user/user.slice';

// Components
import { Typography } from '../Typography';
import { ContentLocalityBadge } from '../ContentLocalityBadge';
import { ObfuscatedText } from '../ObfuscatedText';
import { LocaleLink } from '../LocaleLink';
import { Badge } from '../Badge';
import { ToolTip } from '../ToolTip';
import { ActionModule } from '../ActionModule';
import { ClampContent } from '../ClampContent';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SHARE_OPTIONS, SOURCES } from '../../../constants/base.constants';

// Styles
import * as S from './QuestionCard.styles';
import { ConfettiBurst } from '../ConfettiBurst';


/**
 * Configuration
 */

const ReactParser = new Parser();


/**
 * Component
 */

export const QuestionCard = memo(({
	className, question, source, isPlaceholder, index
}) => {

	// Get current user from hook
	const currentUser = useSelector((state) => state.user.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Generate placeholder
	const questionPlaceholder = useMemo(() => generateTitlePlaceholder(index), [index]);
	const excerptPlaceholder = useMemo(() => generateShortFormContentPlaceholder(index + 1), [index]);

	// Get upvote
	const isUpvoted = useMemo(() => {

		// Get upvote schema
		const upvoteSchema = currentUser.upvote_schema || {};

		// Check for active upvote
		return upvoteSchema[question.question_id] === true;

	}, [currentUser, question]);

	// Create state
	const [isUpvoteActive, setIsUpvoteActive] = useState(isUpvoted === true);
	const [upvoteCount, setUpvoteCount] = useState(question?.upvote_count);
	const [showShareWidget, setShowShareWidget] = useState(false);
	const [fireConfetti, setFireConfetti] = useState(false);

	// Use hooks
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Create refs
	const containerRef = useRef();
	const showShareWidgetRef = useRef();
	const confettiTargetRef = useRef();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Toggle question upvote
	const toggleUpvote = async () => {

		// Handle action for state
		if (!isUpvoteActive) {

			// Update state
			setIsUpvoteActive(true);
			setUpvoteCount(upvoteCount ? upvoteCount + 1 : 1);

			// Fire confetti
			setFireConfetti(true);

			// Reset confetti
			setTimeout(() => {
				setFireConfetti(false);
			}, 3000);

			// Record upvote
			const { data: { result: { user } } } = await recordUpvote({
				source,
				entityType: SOURCES.QUESTION,
				entityId: question.question_id,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}

		} else {

			// Update state
			setIsUpvoteActive(false);
			setUpvoteCount(upvoteCount ? upvoteCount - 1 : 0);

			// Remove upvote
			const { data: { result: { user } } } = await removeUpvote({
				source,
				entityType: SOURCES.QUESTION,
				entityId: question.question_id,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}
		}
	};

	// Handle share flow
	const handleShare = () => {
		setShowShareWidget(!showShareWidget);
	};

	// Handle actions on ref change
	useEffect(() => {

		// Define click outside function
		function handleClickOutside(event) {
			if (showShareWidgetRef.current && containerRef.current && !containerRef.current.contains(event.target)) {
				setShowShareWidget(false);
			}
		}

		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);

		// Return effect
		return () => { // Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};

	}, [containerRef]);

	// Handle actions on upvote state change
	useEffect(() => {

		// Update upvote active
		setIsUpvoteActive(isUpvoted === true);

	}, [isUpvoted]);

	// Handle actions on question upvote count change
	useEffect(() => {

		// Update count
		setUpvoteCount(question?.upvote_count);

	}, [question?.upvote_count]);

	// Handle actions on widget state
	useEffect(() => {

		// Update ref
		showShareWidgetRef.current = showShareWidget;

	}, [showShareWidget]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className} $isPlaceholder={isPlaceholder}>

			{/* Badges */}
			<S.BadgeContainer>

				{/* Partner Badge */}
				{question?.partner && partner?.id && question?.partner.id !== partner?.id && question?.partner.id !== 'core' && (
					<LocaleLink to={`https://${question?.partner.full_domain}`} target="_blank">
						<Badge type="neutral" variation="square" size="small">
							<Typography weight="medium">
								<Trans t={t} i18nKey="communityBadge">
									{{ partnerName: question?.partner.name }}
									{' '}
									Community
								</Trans>
							</Typography>
							<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
						</Badge>
					</LocaleLink>
				)}

			</S.BadgeContainer>

			{/* Content Link */}
			<LocaleLink to={`/answer/${question.slug}`}>

				{/* Question Header */}
				<S.CardHeader>

					{/* Question */}
					<S.QuestionClampContent clamp={6}>
						<Typography tag="h5" weight="bold" style={{ margin: '0px 0px 8px' }} className={isPlaceholder && 'shimmer shimmer-element'}>
							<ObfuscatedText>
								{question?.question_locales?.[locale.localeBaseId] || questionPlaceholder}
							</ObfuscatedText>
						</Typography>
					</S.QuestionClampContent>

					{/* Content Locality Badge */}
					{!isPlaceholder && <ContentLocalityBadge country={question.submitted_country} id={question.question_id} />}

				</S.CardHeader>

				{/* Answer */}
				<ClampContent clamp={12}>
					<Typography tag="p" variation="2" className={isPlaceholder && 'shimmer shimmer-element'}>
						<ObfuscatedText>
							{ReactParser.parse(question?.excerpt_locales?.[locale.localeBaseId]) || excerptPlaceholder}
						</ObfuscatedText>
					</Typography>
				</ClampContent>

			</LocaleLink>

			{/* Action Bar */}
			{!isPlaceholder && (
				<S.ActionBar>

					{/* Action - Upvote */}
					<ActionModule
						onClick={toggleUpvote}
						data-tip={t('questionCard.upvoteTooltip')}
						data-for={`${question.question_id}-upvote`}
					>
						<S.ConfettiTarget ref={confettiTargetRef} />
						<FontAwesomeIcon
							className={isUpvoteActive ? 'up active' : 'up'}
							icon={isUpvoteActive ? ['fas', 'up'] : ['far', 'up']}
						/>
						<Typography
							tag="span"
							className={isUpvoteActive && 'active'}
						>
							{formatNumber(upvoteCount) || '–'}
						</Typography>
					</ActionModule>

					{/* Action - Comment */}
					{question?.comments_allowed && (
						<ActionModule
							onClick={() => { navigate(`${locale.localePath}/answer/${question.slug}/#comments`); }}
						>
							<FontAwesomeIcon className="comment" icon={['far', 'comment']} />
							<Typography tag="span">{formatNumber(question?.comment_count) || '–'}</Typography>
						</ActionModule>
					)}

					{/* Action - Share */}
					<S.ShareActionContainer ref={containerRef}>

						{/* Share Button */}
						<ActionModule
							onClick={handleShare}
						>
							<FontAwesomeIcon className="share" icon={['far', 'share']} />
							<Typography tag="span">
								<Trans t={t} i18nKey="actions.share">
									Share
								</Trans>
							</Typography>
						</ActionModule>

						{/* Share Widget */}
						<S.ShareWidget
							className={showShareWidget ? 'animate show' : 'animate'}
							fromBottom={false}
							closeWidget={() => { setShowShareWidget(false); }}
							source={{
								type: SOURCES.QUESTION,
								id: question.question_id
							}}
							content={{
								title: t('questionCard.shareTitle'),
								description: question?.question_locales?.[locale.localeBaseId],
								url: `https://community.ourwave.org${locale.localePath}/answer/${question.slug}`
							}}
							items={[
								SHARE_OPTIONS.WHATSAPP,
								SHARE_OPTIONS.FACEBOOK,
								SHARE_OPTIONS.LINK,
								SHARE_OPTIONS.TWITTER,
								SHARE_OPTIONS.LINKEDIN,
								SHARE_OPTIONS.REDDIT,
								SHARE_OPTIONS.PINTEREST,
								SHARE_OPTIONS.EMAIL
							]}
						/>

					</S.ShareActionContainer>

					{/* Tooltip */}
					<ToolTip id={`${question.question_id}-upvote`} weight="medium" />

				</S.ActionBar>
			)}

			{/* Confetti */}
			{fireConfetti && (
				<ConfettiBurst
					fireAway={fireConfetti}
					targetRef={confettiTargetRef}
					force={0.1}
					duration={3000}
					particleCount={30}
				/>
			)}
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

QuestionCard.displayName = 'QuestionCard';
QuestionCard.propTypes = {
	className: PropTypes.string,
	question: PropTypes.shape(),
	source: PropTypes.oneOf(Object.values(SOURCES)),
	isPlaceholder: PropTypes.bool,
	index: PropTypes.number
};
QuestionCard.defaultProps = {
	className: null,
	question: {},
	source: null,
	isPlaceholder: false,
	index: 0
};
